import React, { useCallback, useMemo, type Ref } from 'react';
import memoizeOne from 'memoize-one';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItemGroup,
	type OnOpenChangeArgs,
	DropdownItem,
} from '@atlaskit/dropdown-menu';
import { Hide, Show } from '@atlaskit/primitives/responsive';
import ChevronRightLargeIcon from '@atlaskit/icon/core/chevron-right';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isChrome } from '@atlassian/jira-common-util-browser/src/index.tsx';
import { COLUMN_ID_MERGED_COLUMN } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { CATEGORY_TYPE } from '@atlassian/jira-platform-field-config';
import {
	MOVE_COLUMN_FIRST,
	MOVE_COLUMN_LAST,
	MOVE_COLUMN_LEFT,
	MOVE_COLUMN_RIGHT,
	SORT_ASC,
	SORT_DESC,
	SORT_NONE,
} from '../../../../../../common/constants.tsx';
import type { Column, TargetColumnPosition } from '../../../../../../common/types.tsx';
import {
	useIsInlineFieldConfigEnabled,
	useIsMergedColumnEnabled,
	useIsStickyColumnsEnabled,
	useIsTimelineEnabled,
	useIsAutoUnstickyOnSmallScreenEnabled,
	useIsRemoveColumnEnabled,
} from '../../../../../../controllers/features/selectors.tsx';
import { useIFCConfigContext } from '../../../../../../common/ui/inline-field-config/context/index.tsx';
import { useIFCEditField } from '../../../../../../common/ui/inline-field-config/edit-field/index.tsx';
import { useShouldApplyFrozenColumns } from '../../../../../../controllers/use-should-apply-frozen-columns/index.tsx';
import { ActionItem } from './action-item/index.tsx';
import messages from './messages.tsx';

export type Props = {
	column: Column;
	previousColumn?: Column;
	currentColumnWidth: number;
	hasColumnConfiguration: boolean;
	isColumnResizingEnabled: boolean;
	isMergedColumn?: boolean;
	enableDynamicOrderForMergedColumn?: boolean;
	isFirst: boolean;
	isLast: boolean;
	isOpen: boolean;
	triggerRef?: Ref<HTMLButtonElement>;
	onMoveColumn: (position: TargetColumnPosition) => void;
	onOpenChange: (isOpen: boolean) => void;
	onRemoveColumn: () => void;
	onResizeColumn: () => void;
	onFreezeColumn?: () => void;
	onUnfreezeColumn?: () => void;
	onResetColumnWidth: () => void;
	onFocus: () => void;
	onBlur: () => void;
	totalMoveableColumns?: number;
};

const NestedSortingDropdown = ({
	column,
	customMessage,
	onOpenChange,
}: {
	column: Column;
	customMessage: MessageDescriptor;
	onOpenChange: (isOpen: boolean) => void;
}) => {
	const { formatMessage } = useIntl();

	const sortAsc = useCallback(() => {
		column.onSortOrderChanged?.(
			column.jqlTerm,
			SORT_ASC,
			fg('jsc_list_reranking_ast_refactor') ? column.aliases : undefined,
		);
		onOpenChange(false);
	}, [column, onOpenChange]);

	const sortDesc = useCallback(() => {
		column.onSortOrderChanged?.(
			column.jqlTerm,
			SORT_DESC,
			fg('jsc_list_reranking_ast_refactor') ? column.aliases : undefined,
		);
		onOpenChange(false);
	}, [column, onOpenChange]);

	let title = column.title;
	if (column.currentSorting === SORT_ASC && column.ascText) {
		title = `${column.title}: ${formatMessage(messages.sortDirection, {
			sortDescription: column.ascText,
		})}`;
	} else if (column.currentSorting === SORT_DESC && column.descText) {
		title = `${column.title}: ${formatMessage(messages.sortDirection, {
			sortDescription: column.descText,
		})}`;
	}

	return (
		<DropdownMenu
			placement="right-start"
			// We should also enable shouldRenderToParent only in Chrome nested dropdown menu.
			// Mismatched `shouldRenderToParent` settings between parent and nested dropdowns
			// can break the nested menu behavior.
			shouldRenderToParent={isChrome()}
			trigger={({ triggerRef, ...triggerProps }) => (
				<DropdownItem
					{...triggerProps}
					ref={triggerRef}
					elemAfter={<ChevronRightLargeIcon size="small" spacing="spacious" label="" />}
				>
					{title}
				</DropdownItem>
			)}
		>
			<DropdownItemGroup>
				<ActionItem
					actionType="sortAsc"
					onClick={sortAsc}
					isSelected={column.currentSorting === SORT_ASC}
					fieldType={column.type}
				>
					{(column.ascText ?? false)
						? formatMessage(customMessage, {
								sortDescription: column.ascText,
							})
						: formatMessage(messages.sortAsc)}
				</ActionItem>
				<ActionItem
					actionType="sortDesc"
					onClick={sortDesc}
					isSelected={column.currentSorting === SORT_DESC}
					fieldType={column.type}
				>
					{(column.descText ?? false)
						? formatMessage(customMessage, {
								sortDescription: column.descText,
							})
						: formatMessage(messages.sortDesc)}
				</ActionItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

const FreezeColumnOption = ({
	isColumnSticky,
	isSortable,
	hasColumnConfiguration,
	shouldShowNestedSorting,
	onFreezeColumn,
	onUnfreezeColumn,
}: {
	isSortable: boolean;
	hasColumnConfiguration: boolean;
	shouldShowNestedSorting: boolean;
	isColumnSticky?: boolean;
	onFreezeColumn: () => void;
	onUnfreezeColumn: () => void;
}) => {
	const { formatMessage } = useIntl();
	const isAutoUnstickyOnSmallScreenEnabled = useIsAutoUnstickyOnSmallScreenEnabled();
	return (
		<DropdownItemGroup
			hasSeparator={isSortable || hasColumnConfiguration || shouldShowNestedSorting}
		>
			{isAutoUnstickyOnSmallScreenEnabled ? (
				<>
					<Show below="md" as="div">
						<ActionItem actionType="freezeColumn" onClick={onFreezeColumn} isDisabled>
							{formatMessage(messages.freezeColumn)}
						</ActionItem>
					</Show>
					<Hide below="md" as="div">
						<ActionItem
							actionType={isColumnSticky ? 'unfreezeColumn' : 'freezeColumn'}
							onClick={isColumnSticky ? onUnfreezeColumn : onFreezeColumn}
						>
							{formatMessage(isColumnSticky ? messages.unfreezeColumn : messages.freezeColumn)}
						</ActionItem>
					</Hide>
				</>
			) : (
				<ActionItem
					actionType={isColumnSticky ? 'unfreezeColumn' : 'freezeColumn'}
					onClick={isColumnSticky ? onUnfreezeColumn : onFreezeColumn}
				>
					{formatMessage(isColumnSticky ? messages.unfreezeColumn : messages.freezeColumn)}
				</ActionItem>
			)}
		</DropdownItemGroup>
	);
};

export const MoreActions = ({
	column,
	previousColumn,
	currentColumnWidth,
	hasColumnConfiguration,
	isColumnResizingEnabled,
	isMergedColumn,
	enableDynamicOrderForMergedColumn,
	isFirst,
	isLast,
	isOpen,
	triggerRef = null,
	onMoveColumn,
	onOpenChange,
	onRemoveColumn,
	onResizeColumn,
	onFreezeColumn,
	onUnfreezeColumn,
	onResetColumnWidth,
	onFocus,
	onBlur,
	totalMoveableColumns,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();

	const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();
	const isRemoveColumnEnabled = fg('jira_issue_table_remove_column_check')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsRemoveColumnEnabled()
		: true;

	const { ifcConfig } = fg('jira_inline_field_config_gate')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIFCConfigContext()
		: { ifcConfig: null };

	const editField = fg('jira_inline_field_config_gate')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIFCEditField()
		: undefined;

	const sortAsc = useCallback(() => {
		column.onSortOrderChanged?.(
			column.jqlTerm,
			SORT_ASC,
			fg('jsc_list_reranking_ast_refactor') ? column.aliases : undefined,
		);
	}, [column]);

	const sortDesc = useCallback(() => {
		column.onSortOrderChanged?.(
			column.jqlTerm,
			SORT_DESC,
			fg('jsc_list_reranking_ast_refactor') ? column.aliases : undefined,
		);
	}, [column]);

	const clearSorting = useCallback(() => {
		if (isMergedColumn) {
			column.mergedColumns?.forEach((mergedColumn) => {
				if (mergedColumn.currentSorting && mergedColumn.currentSorting !== SORT_NONE) {
					mergedColumn.onSortOrderChanged?.(
						mergedColumn.jqlTerm,
						SORT_NONE,
						fg('jsc_list_reranking_ast_refactor') ? mergedColumn.aliases : undefined,
					);
				}
			});
		} else {
			column.onSortOrderChanged?.(
				column.jqlTerm,
				SORT_NONE,
				fg('jsc_list_reranking_ast_refactor') ? column.aliases : undefined,
			);
		}
	}, [column, isMergedColumn]);

	const handleOnOpenChange = useCallback(
		({ isOpen: newIsOpen }: OnOpenChangeArgs) => {
			if (newIsOpen) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'opened',
						actionSubject: 'dropdown',
					}),
					'issueTableHeaderActions',
				);
			}

			onOpenChange(newIsOpen);
		},
		[createAnalyticsEvent, onOpenChange],
	);

	const mergedColumns = column.mergedColumns ?? [];

	const shouldShowNestedSorting =
		isTimelineEnabled || fg('jira_timeline_harmonisation_m1_issue_table_updates')
			? Boolean(isMergedColumn && mergedColumns.length > 1) &&
				// Ensure that merged columns are sortable before showing the dropdown item group
				mergedColumns.some((mergedCol) => mergedCol.isSortable && mergedCol.onSortOrderChanged)
			: Boolean(isMergedColumn && mergedColumns.length > 1);

	const fieldType =
		Boolean(isMergedColumn) && mergedColumns.length === 1 && fg('jira_list_merged_column_gate')
			? mergedColumns[0]?.type
			: undefined;

	const isColumnSortable = useCallback(
		(col: Column) => Boolean(!!col.onSortOrderChanged && col.isSortable),
		[],
	);

	let isSortable = isColumnSortable(column);
	if (fg('jira_list_merged_column_gate')) {
		isSortable =
			isColumnSortable(column) ||
			Boolean(
				isMergedColumn && !shouldShowNestedSorting && column.mergedColumns?.some(isColumnSortable),
			);
	}

	let isMergedColumnEnabled = false;
	if (fg('jira_list_merged_column_gate')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isMergedColumnEnabled = useIsMergedColumnEnabled();
	}
	let isStickyColumnsEnabled = false;
	if (fg('issue_table_frozen_columns')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isStickyColumnsEnabled = useIsStickyColumnsEnabled();
	}
	let isSmallScreen = false;
	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('issue_table_frozen_columns') && !fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { isSmallScreen: smallScreen } = useShouldApplyFrozenColumns();
		isSmallScreen = smallScreen;
	}

	let canMoveColumnLeft = true;
	if (isMergedColumnEnabled && previousColumn) {
		if (fg('jira-linked-work-items-targeting-gate')) {
			canMoveColumnLeft = enableDynamicOrderForMergedColumn
				? true
				: previousColumn?.id !== COLUMN_ID_MERGED_COLUMN;
		} else {
			canMoveColumnLeft = previousColumn?.id !== COLUMN_ID_MERGED_COLUMN;
		}
	}

	const triggerLabel = formatMessage(messages.columnActions, { column: column.title });

	// Ensure a stable ref function is passed through to the trigger button
	const memoizedMergeRefs = useMemo(() => memoizeOne(mergeRefs), []);

	const customMessage = fg('jira_inline_field_config_gate')
		? messages.sortCustomNew
		: messages.sortCustom;

	const shouldShowRemoveColumnOption = fg('jira_issue_table_remove_column_check')
		? hasColumnConfiguration && isRemoveColumnEnabled
		: hasColumnConfiguration;

	const isCategoryField = column.type === CATEGORY_TYPE;
	/*
		The JWM category field is a bit of a snowflake as it can be used in CMP projects but doesn't support configuration
		in global admin.
		We include this exception here to allow the category field to be updated through inline field configuration as an
		intermediate measure as category will be decommissioned and replaced with the single select custom field type (at
		which point this condition can be removed).
		This is supported in IFC as we call bespoke APIs for updating the category field that support
		CMP projects.
	 */
	const isIfcConfigEnabledNew =
		ifcConfig?.isEnabledForProject || (ifcConfig?.isEnabledForCategoryField && isCategoryField);
	const isIfcConfigEnabled = fg('jsc_list_category_editing_in_nin')
		? isIfcConfigEnabledNew
		: ifcConfig?.IFCTriggerRef;

	// Action dropdown only displays Move column to the last position and Move Column to the first position for 3 or more columns
	const hasMoveToFirstAndLastOptions = fg('jira_timeline_sticky_assignee_status_columns')
		? totalMoveableColumns && totalMoveableColumns > 2
		: true;

	return (
		<DropdownMenu
			testId="native-issue-table.ui.issue-table.header.header-cell.header-cell-actions.more-actions.dropdown-menu"
			isOpen={isOpen}
			onOpenChange={handleOnOpenChange}
			placement="bottom-start"
			trigger={({
				triggerRef: dropdownTriggerRef,
				...triggerProps
			}: CustomTriggerProps<HTMLButtonElement>) => (
				<IconButton
					{...triggerProps}
					ref={memoizedMergeRefs(dropdownTriggerRef, triggerRef)}
					icon={(iconProps) => <ShowMoreHorizontalIcon {...iconProps} size="small" />}
					label={triggerLabel}
					onFocus={onFocus}
					onBlur={onBlur}
					isTooltipDisabled={false}
					spacing="compact"
					appearance="subtle"
					testId="native-issue-table.ui.issue-table.header.header-cell.header-cell-actions.more-actions.icon-button"
				/>
			)}
			// We should only enable shouldRenderToParent in Chrome.
			// Safari and Firefox can clip elements inside a `position: sticky` container if the container isn’t tall enough.
			// This affects dropdowns rendered inside sticky table headers.
			shouldRenderToParent={isChrome()}
		>
			{isSortable && !shouldShowNestedSorting && (
				<DropdownItemGroup>
					{column.currentSorting !== SORT_ASC && (
						<ActionItem actionType="sortAsc" onClick={sortAsc} fieldType={fieldType}>
							{(column.ascText ?? false)
								? formatMessage(customMessage, {
										sortDescription: column.ascText,
									})
								: formatMessage(messages.sortAsc)}
						</ActionItem>
					)}
					{column.currentSorting !== SORT_DESC && (
						<ActionItem actionType="sortDesc" onClick={sortDesc} fieldType={fieldType}>
							{(column.descText ?? false)
								? formatMessage(customMessage, {
										sortDescription: column.descText,
									})
								: formatMessage(messages.sortDesc)}
						</ActionItem>
					)}
					{column.currentSorting !== SORT_NONE && fg('jsc_list_reranking_gate') && (
						<ActionItem actionType="clearSorting" onClick={clearSorting} fieldType={fieldType}>
							{formatMessage(messages.clearSorting)}
						</ActionItem>
					)}
				</DropdownItemGroup>
			)}
			{shouldShowNestedSorting && (
				<DropdownItemGroup title={formatMessage(messages.sortByColumns)}>
					{mergedColumns.map((mergedColumn) =>
						isTimelineEnabled || fg('jira_timeline_harmonisation_m1_issue_table_updates') ? (
							// Ensure that the column is sortable
							mergedColumn.isSortable &&
							mergedColumn.onSortOrderChanged && (
								<NestedSortingDropdown
									key={mergedColumn.id}
									column={mergedColumn}
									customMessage={customMessage}
									onOpenChange={onOpenChange}
								/>
							)
						) : (
							<NestedSortingDropdown
								key={mergedColumn.id}
								column={mergedColumn}
								customMessage={customMessage}
								onOpenChange={onOpenChange}
							/>
						),
					)}
					{mergedColumns.some((col) => col.currentSorting && col.currentSorting !== SORT_NONE) &&
						fg('jsc_list_reranking_gate') && (
							<ActionItem actionType="clearSorting" onClick={clearSorting}>
								{formatMessage(messages.clearSorting)}
							</ActionItem>
						)}
				</DropdownItemGroup>
			)}
			{hasColumnConfiguration && (
				<DropdownItemGroup hasSeparator={isSortable}>
					{!isFirst && (
						<>
							{(!isMergedColumnEnabled ||
								(canMoveColumnLeft && fg('jira_list_merged_column_move_first'))) &&
								hasMoveToFirstAndLastOptions && (
									<ActionItem
										actionType="moveColumnFirst"
										onClick={() => onMoveColumn(MOVE_COLUMN_FIRST)}
									>
										{fg('jira_inline_field_config_gate')
											? formatMessage(messages.moveFirstNew)
											: formatMessage(messages.moveFirst)}
									</ActionItem>
								)}
							{canMoveColumnLeft && (
								<ActionItem
									actionType="moveColumnLeft"
									onClick={() => onMoveColumn(MOVE_COLUMN_LEFT)}
								>
									{fg('jira_inline_field_config_gate')
										? formatMessage(messages.moveLeftNew)
										: formatMessage(messages.moveLeft)}
								</ActionItem>
							)}
						</>
					)}
					{!isLast && (
						<>
							<ActionItem
								actionType="moveColumnRight"
								onClick={() => onMoveColumn(MOVE_COLUMN_RIGHT)}
							>
								{fg('jira_inline_field_config_gate')
									? formatMessage(messages.moveRightNew)
									: formatMessage(messages.moveRight)}
							</ActionItem>
							{hasMoveToFirstAndLastOptions && (
								<ActionItem
									actionType="moveColumnLast"
									onClick={() => onMoveColumn(MOVE_COLUMN_LAST)}
								>
									{fg('jira_inline_field_config_gate')
										? formatMessage(messages.moveLastNew)
										: formatMessage(messages.moveLast)}
								</ActionItem>
							)}
						</>
					)}
					{shouldShowRemoveColumnOption && (
						<ActionItem actionType="removeColumn" onClick={onRemoveColumn}>
							{formatMessage(messages.removeColumn)}
						</ActionItem>
					)}
				</DropdownItemGroup>
			)}
			{
				// eslint-disable-next-line jira/ff/no-preconditioning
				isStickyColumnsEnabled &&
					isMergedColumn &&
					onUnfreezeColumn &&
					onFreezeColumn &&
					fg('issue_table_frozen_columns') &&
					fg('jira_list_frozen_column_bugfix') && (
						<FreezeColumnOption
							onFreezeColumn={onFreezeColumn}
							onUnfreezeColumn={onUnfreezeColumn}
							isColumnSticky={column.isColumnSticky}
							isSortable={isSortable}
							hasColumnConfiguration={hasColumnConfiguration}
							shouldShowNestedSorting={shouldShowNestedSorting}
						/>
					)
			}
			{
				// eslint-disable-next-line jira/ff/no-preconditioning
				isStickyColumnsEnabled &&
					isMergedColumn &&
					onUnfreezeColumn &&
					onFreezeColumn &&
					fg('issue_table_frozen_columns') &&
					!fg('jira_list_frozen_column_bugfix') && (
						<DropdownItemGroup
							hasSeparator={isSortable || hasColumnConfiguration || shouldShowNestedSorting}
						>
							{isSmallScreen ? (
								<ActionItem actionType="freezeColumn" onClick={onFreezeColumn} isDisabled>
									{formatMessage(messages.freezeColumn)}
								</ActionItem>
							) : (
								<ActionItem
									actionType={column.isColumnSticky ? 'unfreezeColumn' : 'freezeColumn'}
									onClick={column.isColumnSticky ? onUnfreezeColumn : onFreezeColumn}
								>
									{formatMessage(
										column.isColumnSticky ? messages.unfreezeColumn : messages.freezeColumn,
									)}
								</ActionItem>
							)}
						</DropdownItemGroup>
					)
			}
			{isColumnResizingEnabled && (
				<DropdownItemGroup
					hasSeparator={
						isMergedColumn && fg('issue_table_frozen_columns')
							? false
							: isSortable || hasColumnConfiguration || shouldShowNestedSorting
					}
				>
					{isMergedColumn && fg('issue_table_frozen_columns')}
					<ActionItem actionType="resizeColumn" onClick={onResizeColumn}>
						{formatMessage(messages.resizeColumn)}
					</ActionItem>
					{currentColumnWidth != null &&
						currentColumnWidth !== column.defaultWidth &&
						!isMergedColumn && (
							<ActionItem actionType="resetColumnWidth" onClick={onResetColumnWidth}>
								{formatMessage(messages.resetColumnWidth)}
							</ActionItem>
						)}
				</DropdownItemGroup>
			)}
			{isInlineFieldConfigEnabled &&
				column.configureableFieldId &&
				ifcConfig &&
				isIfcConfigEnabled &&
				editField && (
					<DropdownItemGroup hasSeparator>
						<ifcConfig.IFCTriggerRef>
							{({ triggerRef: ifcTriggerRef }) => (
								<ActionItem
									ref={ifcTriggerRef}
									actionType="editField"
									onClick={() => {
										if (column.configureableFieldId) {
											editField(column.configureableFieldId, column.id);
										}
									}}
								>
									{formatMessage(messages.editField)}
								</ActionItem>
							)}
						</ifcConfig.IFCTriggerRef>
					</DropdownItemGroup>
				)}
			<ShortcutScope />
		</DropdownMenu>
	);
};
